// client/src/pages/Login.tsx - PROFESSIONAL THEME
import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../supabase';

interface LocationState {
    from?: { pathname: string };
}

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const fromPath = (location.state as LocationState)?.from?.pathname ?? '/';

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [showTypeSelection, setShowTypeSelection] = useState(false);

    useEffect(() => {
        // Check if user is already logged in (e.g. from Google OAuth redirect)
        const checkSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.user) return;

            const user = session.user;
            const userType = user.user_metadata?.user_type;

            // Check for existing profiles
            const { data: expert } = await supabase.from('experts').select('id').eq('user_id', user.id).maybeSingle();
            if (expert) { navigate('/expert-dashboard'); return; }

            const { data: attorney } = await supabase.from('attorneys').select('id').eq('user_id', user.id).maybeSingle();
            if (attorney) { navigate('/attorney-dashboard'); return; }

            // Has user type but no profile - go to intake
            if (userType === 'expert') { navigate('/expert-intake'); return; }
            if (userType === 'attorney') { navigate('/waitlist'); return; }

            // No user type (new Google user) - show type selection
            setShowTypeSelection(true);
        };
        checkSession();
    }, []);

    const handleLogin = async (e: FormEvent) => {
        e.preventDefault();
        setErrorMsg('');
        setLoading(true);
        //setDebugInfo('Starting login process...');

        try {

            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });
            //setDebugInfo(`Login response: ${JSON.stringify({ user: data?.user?.id, session: !!data?.session, error: error?.message })}`);

            if (error) {
                if (error.message.includes('Email not confirmed')) {
                    setErrorMsg('Please verify your email address before logging in. Check your email for a verification link.');
                } else if (error.message.includes('Invalid login credentials')) {
                    setErrorMsg('Invalid email or password. Please check your credentials and try again.');
                } else {
                    setErrorMsg(error.message);
                }
                setLoading(false);
                return;
            }

            if (!data?.user || !data?.session) {
                setErrorMsg('Login failed - no user or session returned');
                setLoading(false);
                return;
            }

            if (!data.user.email_confirmed_at) {
                setErrorMsg('Please verify your email address before logging in. Check your email for a verification link.');
                setLoading(false);
                return;
            }
            //setDebugInfo('Login successful, checking user profile...');

            const user = data.user;
            const userType = user.user_metadata?.user_type;

            // Check admin first
            const { data: isAdmin } = await supabase.rpc('check_is_admin');
            if (isAdmin) {
                navigate('/admin-verification');
                setLoading(false);
                return;
            }
            const { data: expert, error: expertError } = await supabase
                .from('experts')
                .select('id, full_name, is_verified')
                .eq('user_id', user.id)
                .maybeSingle();

            if (expert) {
                //setDebugInfo('User identified as expert, redirecting...');
                navigate('/expert-dashboard');
                setLoading(false);
                return;
            }
            const { data: attorney, error: attorneyError } = await supabase
                .from('attorneys')
                .select('id, name, is_verified')
                .eq('user_id', user.id)
                .maybeSingle();

            if (attorney) {
                //setDebugInfo('User identified as attorney, redirecting...');
                navigate('/attorney-dashboard');
                setLoading(false);
                return;
            }
            if (userType === 'expert') {
                //setDebugInfo('Expert user without profile, redirecting to intake...');
                navigate('/expert-intake');
            } else if (userType === 'attorney') {
                navigate('/waitlist');
            } else {
                // No user type (Google login) - show type selection
                setLoading(false);
                setShowTypeSelection(true);
                return;
            }


        } catch (err: any) {
            setErrorMsg(`Unexpected error: ${err.message}`);
            //setDebugInfo(`Error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const resendVerificationEmail = async () => {
        if (!email) {
            setErrorMsg('Please enter your email address first');
            return;
        }

        try {
            const { error } = await supabase.auth.resend({
                type: 'signup',
                email: email,
                options: {
                    emailRedirectTo: `${window.location.origin}/confirm-signup`
                }
            });

            if (error) {
                setErrorMsg('Error sending verification email: ' + error.message);
            } else {
                setErrorMsg('Verification email sent! Please check your inbox and spam folder.');
            }
        } catch (error) {
            console.error('Resend verification error:', error);
            setErrorMsg('Error sending verification email');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 font-body">
            <div className="max-w-md w-full">
                {showTypeSelection && (
                    <div className="bg-white rounded-2xl shadow-card p-8 border border-gray-100 mb-6">
                        <h2 className="font-display text-2xl font-bold text-primary-900 text-center mb-2">Welcome!</h2>
                        <p className="text-gray-600 text-center mb-6">Tell us about yourself to get started</p>
                        <div className="grid grid-cols-2 gap-4">
                            <button
                                onClick={async () => {
                                    try {
                                        const { data: { user } } = await supabase.auth.getUser();
                                        if (user) {
                                            await supabase.auth.updateUser({ data: { user_type: 'attorney' } });
                                        }
                                    } catch (e) {}
                                    navigate('/waitlist');
                                }}
                                className="p-6 rounded-xl border-2 border-gray-200 hover:border-primary-600 hover:bg-primary-50 transition-all text-center"
                            >
                                <svg className="w-10 h-10 mx-auto mb-3 text-primary-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                                </svg>
                                <span className="font-semibold text-primary-900">Legal Professional</span>
                            </button>
                            <button
                                onClick={async () => {
                                    try {
                                        const { data: { user } } = await supabase.auth.getUser();
                                        if (user) {
                                            await supabase.auth.updateUser({ data: { user_type: 'attorney' } });
                                        }
                                    } catch (e) {}
                                    navigate('/waitlist');
                                }}
                                className="p-6 rounded-xl border-2 border-gray-200 hover:border-secondary-600 hover:bg-secondary-50 transition-all text-center"
                            >
                                <svg className="w-10 h-10 mx-auto mb-3 text-secondary-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                </svg>
                                <span className="font-semibold text-secondary-900">Medical Expert</span>
                            </button>
                        </div>
                    </div>
                )}
                {!showTypeSelection && (
                    <div className="bg-white rounded-2xl shadow-card p-8 border border-gray-100">
                        <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-gradient-to-br from-primary-800 to-primary-900 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </div>
                        <h2 className="font-display text-3xl font-bold text-primary-900">Welcome Back</h2>
                        <p className="mt-2 text-gray-600">Sign in to Expert Link Pro</p>
                    </div>
                    <form onSubmit={handleLogin} className="space-y-6">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                Email Address
                            </label>
                            <input
                                id="email"
                                type="email"
                                required
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                placeholder="you@example.com"
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                Password
                            </label>
                            <input
                                id="password"
                                type="password"
                                required
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                placeholder="••••••••"
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    type="checkbox"
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                                    Remember me
                                </label>
                            </div>

                            <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                                Forgot password?
                            </Link>
                        </div>

                        {errorMsg && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
                                {errorMsg}
                                {errorMsg.includes('verify your email') && (
                                    <div className="mt-2">
                                        <button
                                            type="button"
                                            onClick={resendVerificationEmail}
                                            className="text-sm text-primary-600 hover:text-primary-700 underline font-medium"
                                        >
                                            Resend verification email
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full py-3 px-4 rounded-xl text-white font-semibold transition-all duration-200 ${
                                loading
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-primary-800 to-primary-900 hover:from-primary-900 hover:to-primary-950 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                            }`}
                        >
                            {loading ? 'Signing in...' : 'Sign In'}
                        </button>

                        <p className="text-center text-sm text-gray-600">
                            Don't have an account?{' '}
                            <Link to="/signup" className="font-semibold text-primary-600 hover:text-primary-700">
                                Create account
                            </Link>
                        </p>

                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                            <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-gray-500">or</span></div>
                        </div>
                        <button
                            type="button"
                            onClick={async () => {
                                await supabase.auth.signInWithOAuth({
                                    provider: 'google',
                                    options: { redirectTo: `${window.location.origin}/login` }
                                });
                            }}
                            className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-white border-2 border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-all"
                        >
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            Continue with Google
                        </button>
                    </form>


                </div>
                )}
            </div>
        </div>
    );
}